import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './routing/app-routing.module';
import { AppComponent } from './app.component';
import { NewUserModule } from './modules/new-user/new-user.module';
import { PrivacyPolicyModule } from './modules/privacy-policy/privacy-policy.module';
import { RegisteredUserModule } from './modules/registered-user/registered-user.module';
import { SubscribedUserModule } from './modules/subscribed-user/subscribed-user.module';
import { TermsAndConditionsModule } from './modules/terms-and-conditions/terms-and-conditions.module';
import { EarlyAccessModule } from './modules/early-access/early-access.module';
import { SharedModule } from './shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { StorageService } from './core/services/local-storage/storage.service';
import { STORAGE_TOKEN } from './core/services/local-storage/storage.type';
import { AuthConfig } from './core/services/auth/auth.config';
import { APIAuthFlowService } from './core/services/API/APIAuthFlow.service';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APIJoinFlowService } from './core/services/API/APIJoinFlow.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TokenService } from './core/services/auth/token.service';
import { UserService } from './core/services/user/user.service';
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { ErrorComponent } from './pages/error/error.component';
import { RoutingUtils } from './routing/routing.utils';
import { environment } from '../environments/environment';
import { RuntimeMode } from '../environments/environment.type';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { FormService } from './core/services/user/form.service';
import { DatePipe } from '@angular/common';
import { AuthService } from './core/services/auth/auth.service';
import { ErrorService } from './core/services/error/error.service';
import * as Sentry from '@sentry/angular-ivy';
import { Router } from '@angular/router';
import { LoginDialogComponent } from './pages/login-dialog/login-dialog.component';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { LogoutComponent } from './pages/logout/logout.component';
import { DialogService } from './core/services/dialog/dialog.service';
import { CookieService } from 'ngx-cookie-service';
import { WelcomeModule } from './modules/welcome/welcome.module';

let gtmImports = [];
let gtmProviders = [];

if (environment.includeGTMTag) {
  console.log('Including GTM imports & providers');
  gtmImports = [
    GoogleTagManagerModule.forRoot({
      id: environment.gtmId,
    }),
  ];

  gtmProviders = [
    {
      provide: 'googleTagManagerId',
      useValue: environment.gtmId,
    },
  ];
}

@NgModule({
  declarations: [AppComponent, ErrorComponent, LoginDialogComponent, LogoutComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    WelcomeModule,
    EarlyAccessModule,
    NewUserModule,
    PrivacyPolicyModule,
    RegisteredUserModule,
    SubscribedUserModule,
    TermsAndConditionsModule,
    SharedModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    MaterialModule,
    LoggerModule.forRoot({
      level:
        environment.runtime === RuntimeMode.DEVELOPMENT
          ? NgxLoggerLevel.DEBUG
          : NgxLoggerLevel.INFO,
      timestampFormat: 'HH:mm:ss.SSS',
      enableSourceMaps:
        environment.runtime === RuntimeMode.DEVELOPMENT ? true : false,
    }),
    ...gtmImports,
  ],
  providers: [
    { provide: STORAGE_TOKEN, useClass: StorageService },
    {
      provide: AuthConfig,
      useFactory: (logger: NGXLogger) => AuthConfig.getInstance(logger),
      deps: [NGXLogger],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false, // If we set showDialog: true, a Sentry error Reporting Dialogue will pop up upon every error
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    ...gtmProviders,
    DatePipe,
    TokenService,
    AuthService,
    APIAuthFlowService,
    APIJoinFlowService,
    UserService,
    RoutingUtils,
    FormService,
    ErrorService,
    AnalyticsService,
    DialogService,
    CookieService
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
